/**
 * Age-group calendar colour constants — seed palettes and legacy paint bridge.
 */

export const DEFAULT_UNGROUPED_CALENDAR_COLOR = '#64748b' as const;

/** Default calendar colours for age-range groups (under 13 vs 13+). */
export const AGE_RANGE_JUNIOR_CALENDAR_COLOR = '#6366f1' as const;
export const AGE_RANGE_ADULT_CALENDAR_COLOR = DEFAULT_UNGROUPED_CALENDAR_COLOR;

/** Age ranges that include anyone this age or older use the adult calendar colour. */
export const AGE_RANGE_ADULT_THRESHOLD_AGE = 13;

export function ageRangeIncludesAdultAges(
  minAge?: number | null,
  maxAge?: number | null
): boolean {
  if (minAge != null && minAge >= AGE_RANGE_ADULT_THRESHOLD_AGE) return true;
  if (maxAge != null && maxAge >= AGE_RANGE_ADULT_THRESHOLD_AGE) return true;
  return false;
}

export function resolveAgeRangeCalendarColor(
  minAge?: number | null,
  maxAge?: number | null
): string {
  return ageRangeIncludesAdultAges(minAge, maxAge)
    ? AGE_RANGE_ADULT_CALENDAR_COLOR
    : AGE_RANGE_JUNIOR_CALENDAR_COLOR;
}

/** Legacy lessons.ball_color enum → hex (temporary paint bridge until column drop). */
export const LEGACY_BALL_COLOR_TO_HEX: Record<string, string> = {
  red: '#ef4444',
  orange: '#f97316',
  green: '#22c55e',
  yellow: '#eab308',
  blue: '#3b82f6',
};

/** Hot Shots seed labels + preset calendar colours (editable after seed). */
export const HOT_SHOTS_SEED_GROUPS = [
  { label: 'Red Ball (Stage 3)', calendarColor: '#ef4444' },
  { label: 'Orange Ball (Stage 2)', calendarColor: '#f97316' },
  { label: 'Green Ball (Stage 1)', calendarColor: '#22c55e' },
  { label: 'Yellow Ball', calendarColor: '#eab308' },
] as const;

/** Tennis ball-colour presets for manual age-group creation (Hot Shots + blue). */
export const TENNIS_BALL_COLOR_PRESETS = [
  { key: 'red', shortLabel: 'Red', ...HOT_SHOTS_SEED_GROUPS[0] },
  { key: 'orange', shortLabel: 'Orange', ...HOT_SHOTS_SEED_GROUPS[1] },
  { key: 'green', shortLabel: 'Green', ...HOT_SHOTS_SEED_GROUPS[2] },
  { key: 'yellow', shortLabel: 'Yellow', ...HOT_SHOTS_SEED_GROUPS[3] },
  { key: 'blue', shortLabel: 'Blue', label: 'Blue Ball', calendarColor: '#3b82f6' },
] as const;

export type TennisBallColorPresetKey = (typeof TENNIS_BALL_COLOR_PRESETS)[number]['key'];

export function matchTennisBallColorPreset(
  label: string,
  calendarColor: string
): TennisBallColorPresetKey | null {
  const normalizedColor = calendarColor.trim().toLowerCase();
  const match = TENNIS_BALL_COLOR_PRESETS.find(
    (preset) =>
      preset.label === label.trim() && preset.calendarColor.toLowerCase() === normalizedColor
  );
  return match?.key ?? null;
}

/** Age-range seed labels — calendar colour is derived from min/max (junior vs adult). */
export const AGE_RANGE_SEED_GROUPS = [
  { label: 'Little Stars (4-6)', minAge: 4, maxAge: 6 },
  { label: 'Juniors (7-10)', minAge: 7, maxAge: 10 },
  { label: 'Intermediates (11-14)', minAge: 11, maxAge: 14 },
  { label: 'High Performance (15-18)', minAge: 15, maxAge: 18 },
  { label: 'Adults (18+)', minAge: 18 },
] as const;

/** Custom seed labels + preset calendar colours. */
export const CUSTOM_SEED_GROUPS = [
  { label: 'Juniors', calendarColor: '#6366f1' },
  { label: 'Adults', calendarColor: '#64748b' },
] as const;

/** Palette for backfill / extra groups by sort_order % length. */
export const AGE_GROUP_STARTER_HEX_PALETTE = [
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#14b8a6',
  '#f59e0b',
  '#6366f1',
] as const;

export const CALENDAR_COLOR_HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

export function isValidCalendarColorHex(value: string | null | undefined): value is string {
  return !!value && CALENDAR_COLOR_HEX_REGEX.test(value);
}

export function starterHexForSortOrder(sortOrder: number): string {
  const palette = AGE_GROUP_STARTER_HEX_PALETTE;
  return palette[((sortOrder % palette.length) + palette.length) % palette.length];
}
